import type { GetClassResponse } from "@/openapi/generated";

export type ClassAvailabilityTone = "open" | "full" | "waitlist" | "neutral";

export interface ClassAvailabilityDisplay {
  label: string;
  tone: ClassAvailabilityTone;
  spotsLeft: number | null;
}

/** Human-readable enrollment status for public class pages. */
export function getClassAvailabilityDisplay(
  cls: Pick<
    GetClassResponse,
    "maxNumberOfStudents" | "currentNumberOfStudents" | "hasWaitList"
  >,
): ClassAvailabilityDisplay | null {
  const max =
    cls.maxNumberOfStudents != null ? Number(cls.maxNumberOfStudents) : null;
  if (max == null || Number.isNaN(max)) return null;

  const current =
    cls.currentNumberOfStudents != null
      ? Number(cls.currentNumberOfStudents)
      : null;

  if (current != null && !Number.isNaN(current)) {
    const spotsLeft = Math.max(0, max - current);
    if (spotsLeft === 0) {
      return cls.hasWaitList
        ? {
            label: "Full - waitlist available",
            tone: "waitlist",
            spotsLeft: 0,
          }
        : {
            label: "Class full",
            tone: "full",
            spotsLeft: 0,
          };
    }
    return {
      label: `${spotsLeft} spot${spotsLeft === 1 ? "" : "s"} available`,
      tone: "open",
      spotsLeft,
    };
  }

  return {
    label: `Up to ${max} students`,
    tone: "neutral",
    spotsLeft: null,
  };
}

type ClassEnrollmentCounts = {
  maxNumberOfStudents?: unknown;
  currentNumberOfStudents?: unknown;
};

/** True when another student can take a seat. No maximum means the class is open. */
export function classHasOpenSpot(cls: ClassEnrollmentCounts): boolean {
  if (cls.maxNumberOfStudents == null) return true;
  const max = Number(cls.maxNumberOfStudents);
  const current = Number(cls.currentNumberOfStudents ?? 0);
  if (!Number.isFinite(max) || max < 0) return true;
  if (!Number.isFinite(current)) return true;
  return current < max;
}

/** True when current enrollment exceeds class capacity (force-enroll overage). */
export function isClassOverEnrolled(cls: ClassEnrollmentCounts): boolean {
  if (cls.maxNumberOfStudents == null) return false;
  const max = Number(cls.maxNumberOfStudents);
  const current = Number(cls.currentNumberOfStudents ?? 0);
  if (!Number.isFinite(max) || max < 0) return false;
  if (!Number.isFinite(current)) return false;
  return current > max;
}

export function formatClassEnrollmentCount(cls: ClassEnrollmentCounts): string {
  const current = Number(cls.currentNumberOfStudents ?? 0);
  const currentLabel = Number.isFinite(current) ? String(current) : "0";
  if (cls.maxNumberOfStudents == null) {
    return `${currentLabel} / ∞`;
  }
  const max = Number(cls.maxNumberOfStudents);
  return `${currentLabel} / ${Number.isFinite(max) ? String(max) : "∞"}`;
}

/** Human-readable enrollment status for public class pages. */
export function getClassAvailabilityLabel(
  cls: Pick<
    GetClassResponse,
    "maxNumberOfStudents" | "currentNumberOfStudents" | "hasWaitList"
  >,
): string | null {
  return getClassAvailabilityDisplay(cls)?.label ?? null;
}

/** Short label for listing rows (e.g. "3 spots", "Waitlist", "Full"). */
export function getClassAvailabilityCompactLabel(
  display: ClassAvailabilityDisplay,
): string {
  if (display.spotsLeft != null && display.spotsLeft > 0) {
    return `${display.spotsLeft} ${display.spotsLeft === 1 ? "spot" : "spots"}`;
  }
  if (display.tone === "waitlist") return "Waitlist";
  if (display.tone === "full") return "Full";
  return display.label;
}
